
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import MultiSelect from 'primevue/multiselect';
import Checkbox from 'primevue/checkbox';
import { getTableName } from '@/worker/db/sqlHelpers';
import {
	countLateInvoices,
	groupKoCardsByDocNo,
} from './creditLimitCalculator';

@Component({
	components: { TableFilterable, MultiSelect, Checkbox },
})
export default class RecommendationCreditLimitAnalysisTable extends FilterWidget {
	@Prop({ default: '' }) public readonly selectedAgent!: string;

	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: Array<{
		customer: string;
		total_sales: number;
		sales_month_count: number;
		average_sales: number;
		due_inv_count: number;
		late_due_inv_count: number;
		late_percentage: number;
		tier: string;
		recommend_limit: number;
		current_limit: number;
		new_limit: number | null;
		last_review: number | null;
		status: string;
	}> = [];
	public tableFields: any[] = [];

	public isShowExtraDetails: boolean = false;
	public isOnlyFilledNewLimit: boolean = false;
	public selectedStatus: string[] = [];
	public selectedStatusTypeList: Array<ValueText<string>> = [];

	@Watch('selectedStatusTypeList', { immediate: true })
	public onSelectedStatusTypeListChanged() {
		this.selectedStatus = this.selectedStatusTypeList.map((vt) => vt.value);
	}

	public get exportFileName() {
		const formattedDate = [
			moment(this.selectedDateRange[0]).format('DD MMM YY'),
			moment(this.selectedDateRange[1]).format('DD MMM YY'),
		];
		return (
			'Credit Limit Analysis' + '_' + formattedDate[0] + ' to ' + formattedDate[1]
		);
	}

	public get statusType() {
		return this.selectedStatusTypeList;
	}

	public get showFilledNewLimitItems() {
		const newItems: any[] = [];

		if (this.isOnlyFilledNewLimit) {
			this.tableItems.forEach((item) => {
				if (
					item.hasOwnProperty('new_limit') &&
					(item.new_limit || item.new_limit === 0)
				) {
					if (this.selectedStatus.includes(item.status)) {
						newItems.push(item);
					}
				}
			});

			return newItems.length > 0
				? newItems
				: this.tableItems.filter((item) =>
						this.selectedStatus.includes(item.status),
				  );
		} else {
			return this.tableItems.filter((item) =>
				this.selectedStatus.includes(item.status),
			);
		}
	}

	public get showExtraDetailFields() {
		return this.isShowExtraDetails
			? [
					{
						key: 'customer',
						sortable: true,
						tdClass: (value) => {
							if (value) {
								return 'custom-style';
							}
						},
						stickyColumn: true,
					},
					{
						key: 'total_sales',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
						variant: 'info',
					},
					{
						key: 'sales_month_count',
						label: 'Mth w Sales',
						sortable: true,
						variant: 'info',
					},
					{
						key: 'average_sales',
						label: 'TTM Av. Mthly Sales',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
					{
						key: 'due_inv_count',
						label: 'Due Invoice',
						sortable: true,
						variant: 'info',
					},
					{
						key: 'late_due_inv_count',
						label: 'Late On Due Invoice',
						sortable: true,
						variant: 'info',
					},
					{
						key: 'late_percentage',
						label: '% Late',
						sortable: true,
						variant: 'info',
					},
          {
            key: 'previous_tier',
            sortable: true,
          },
					{
						key: 'tier',
						sortable: true,
					},
					{
						key: 'recommend_limit',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
					{
						key: 'current_limit',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
					{
						key: 'new_limit',
						sortable: true,
					},
					{
						key: 'last_review',
						sortable: true,
						formatter: TableItemFormatter.date3,
					},
			  ]
			: [
					{
						key: 'customer',
						sortable: true,
						tdClass: (value) => {
							if (value) {
								return 'custom-style';
							}
						},
						stickyColumn: true,
					},
					{
						key: 'average_sales',
						label: 'TTM Av.Sales - Per Month',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
          {
            key: 'previous_tier',
            sortable: true,
          },
					{
						key: 'tier',
						sortable: true,
					},
					{
						key: 'recommend_limit',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
					{
						key: 'current_limit',
						sortable: true,
						formatter: TableItemFormatter.currencySplit,
					},
					{
						key: 'new_limit',
						sortable: true,
					},
					{
						key: 'last_review',
						sortable: true,
						formatter: TableItemFormatter.date3,
					},
			  ];
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedAgent, selectedDateRange } = this;
		return JSON.stringify([selectedAsOfDate, selectedAgent, selectedDateRange]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.35);

		const dateRangeStart = this.selectedDateRange[0];
		const dateRangeEnd = this.selectedDateRange[1];
		const oneYearMs = 31556926000;

		// Build manual filter clauses
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let agentFilter = '';
		if (this.selectedAgent) {
			agentFilter = ` AND "AGENT" = '${escapeStr(this.selectedAgent)}'`;
		}

		const globalTable = getTableName('diyGlobalCustomers');
		const ivdnTable = getTableName('customerIvDns');
		const koTable = getTableName('customerKos');

		const queryHead = await this._ensureTablesLoaded([
			'diyGlobalCustomers', 'customerIvDns', 'customerKos',
		] as any[]);
		this.checkInterrupted(jobId);

		// Get status list and global customer data in parallel
		const [statusResult, globalResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT DISTINCT "STATUS" as status
				FROM ${globalTable}
				WHERE 1=1${agentFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					TRY_CAST("CREDITLIMIT" AS DOUBLE) as credit_limit,
					TRY_CAST("CL_LASTREVIEW" AS BIGINT) as last_review_date,
					"STATUS" as status
				FROM ${globalTable}
				WHERE 1=1${agentFilter}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.35, 0.55);

		// Build status type list
		const statusTypeList = globalResult
			.map((row: any) => row.status)
			.filter((v: any, i: number, a: any[]) => a.indexOf(v) === i);
		this.selectedStatusTypeList = statusTypeList.map((item: string) => ({
			value: item,
			text:
				item === 'A'
					? 'ACTIVE'
					: item === 'N'
					? 'PENDING'
					: item === 'I'
					? 'INACTIVE'
					: item === 'P'
					? 'PROSPECT'
					: item === 'S'
					? 'SUSPEND'
					: item,
		}));

		// Group global customers
		const groupedGlobalCustomer: Record<string, any[]> = {};
		for (const row of globalResult) {
			if (!groupedGlobalCustomer[row.code]) {
				groupedGlobalCustomer[row.code] = [];
			}
			groupedGlobalCustomer[row.code].push({
				code: row.code,
				creditLimit: Number(row.credit_limit) || 0,
				lastReviewDate: row.last_review_date ? Number(row.last_review_date) : null,
				status: row.status,
			});
		}

		const customerList = Object.keys(groupedGlobalCustomer);

		if (customerList.length === 0) {
			this.tableItems = [];
			this.saveHistory('tableItems', 'tableFields');
			return;
		}

		const customerFilter = this.selectedAgent
			? ` AND "CODE" IN (
				SELECT "CODE"
				FROM ${globalTable}
				WHERE 1=1${agentFilter}
			)`
			: '';

		const oneYearStart = dateRangeStart - oneYearMs;
		this.generateLoadingText(0.55, 0.9);

		// Query all data in parallel
		const [ivdnResult, dueIvdnResult, koResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					"DOCNO" as doc_no,
					TRY_CAST("DOCDATE" AS BIGINT) as doc_date,
					TRY_CAST("DUEDATE" AS BIGINT) as due_date,
					TRY_CAST("LOCALDOCAMT" AS DOUBLE) as doc_amount
				FROM ${ivdnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${oneYearStart} AND ${dateRangeEnd}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					"DOCNO" as doc_no,
					TRY_CAST("DOCDATE" AS BIGINT) as doc_date,
					TRY_CAST("DUEDATE" AS BIGINT) as due_date,
					TRY_CAST("LOCALDOCAMT" AS DOUBLE) as doc_amount
				FROM ${ivdnTable}
				WHERE TRY_CAST("DUEDATE" AS BIGINT) >= ${oneYearStart}
					AND TRY_CAST("DUEDATE" AS BIGINT) <= ${dateRangeEnd}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					"TODOCNO" as to_doc_no,
					TRY_CAST("KOLOCALAMT" AS DOUBLE) as ko_amount,
					TRY_CAST("KOGAINLOSS" AS DOUBLE) as ko_gain_loss,
					TRY_CAST("FROMDOCDATE" AS BIGINT) as ko_date
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${this.selectedAsOfDate}
					${customerFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		// Convert results to typed arrays
		const ivdnCards = ivdnResult.map((row: any) => ({
			code: row.code,
			docNo: row.doc_no,
			date: Number(row.doc_date) || 0,
			dueDate: Number(row.due_date) || 0,
			amount: Number(row.doc_amount) || 0,
		}));

		const dueIvdnCards = dueIvdnResult.map((row: any) => ({
			code: row.code,
			docNo: row.doc_no,
			date: Number(row.doc_date) || 0,
			dueDate: Number(row.due_date) || 0,
			amount: Number(row.doc_amount) || 0,
		}));

		const koCards = koResult.map((row: any) => ({
			code: row.code,
			toDocNo: row.to_doc_no,
			amount: Number(row.ko_amount) || 0,
			gainLoss: Number(row.ko_gain_loss) || 0,
			koDate: Number(row.ko_date) || 0,
		}));
		const koByDocNo = groupKoCardsByDocNo(koCards);

		// Group IVDN cards by customer and period
		const groupedIvdnCustomer: Record<string, any[]> = {};
		const groupedDueIvdnCustomer: Record<string, any[]> = {};
		const prevGroupedIvdnCustomer: Record<string, any[]> = {};
		const prevGroupedDueIvdnCustomer: Record<string, any[]> = {};

		for (const ivdn of ivdnCards) {
			if (ivdn.date > dateRangeStart) {
				if (!groupedIvdnCustomer[ivdn.code]) {
					groupedIvdnCustomer[ivdn.code] = [];
				}
				groupedIvdnCustomer[ivdn.code].push(ivdn);
			} else {
				if (!prevGroupedIvdnCustomer[ivdn.code]) {
					prevGroupedIvdnCustomer[ivdn.code] = [];
				}
				prevGroupedIvdnCustomer[ivdn.code].push(ivdn);
			}
		}

		for (const ivdn of dueIvdnCards) {
			if (ivdn.date > dateRangeStart) {
				if (!groupedDueIvdnCustomer[ivdn.code]) {
					groupedDueIvdnCustomer[ivdn.code] = [];
				}
				groupedDueIvdnCustomer[ivdn.code].push(ivdn);
			} else {
				if (!prevGroupedDueIvdnCustomer[ivdn.code]) {
					prevGroupedDueIvdnCustomer[ivdn.code] = [];
				}
				prevGroupedDueIvdnCustomer[ivdn.code].push(ivdn);
			}
		}

		this.generateLoadingText(0.95);

		const result: Array<{
			customer: string;
			total_sales: number;
			sales_month_count: number;
			average_sales: number;
			due_inv_count: number;
			late_due_inv_count: number;
			late_percentage: number;
			tier: string;
			recommend_limit: number;
			current_limit: number;
			last_review: number | null;
			new_limit: number | null;
			status: string;
		}> = [];

		for (const code in groupedGlobalCustomer) {
			if (
				groupedIvdnCustomer.hasOwnProperty(code) &&
				groupedDueIvdnCustomer.hasOwnProperty(code)
			) {
				const cards = groupedIvdnCustomer[code];
				const dueCards = groupedDueIvdnCustomer[code];

				const prevCards = prevGroupedIvdnCustomer[code];
				const prevDueCards = prevGroupedDueIvdnCustomer[code];

				const cards2 = groupedGlobalCustomer[code];

				const dueCount = cards.length;
				const prevDueCount = prevCards ? prevCards.length : 0;

				const salesMonthCount = [
					...new Set(cards.map((ivdn: any) => moment(ivdn.date).format('MMM YY'))),
				].length;

				const prevSalesMonthCount = prevCards ? [
					...new Set(prevCards.map((ivdn: any) => moment(ivdn.date).format('MMM YY'))),
				].length : 0;

				const totalIvdn = cards.reduce((a: number, b: any) => a + b.amount, 0);

				const prevTotalIvdn = prevCards ? prevCards.reduce((a: number, b: any) => a + b.amount, 0) : 0;

				const averageSales = totalIvdn / salesMonthCount;

				const prevAverageSales = prevCards ? prevTotalIvdn / prevSalesMonthCount : 0;

				const object = {
					customer: gdbx.customerNames[code],
					total_sales: totalIvdn,
					sales_month_count: salesMonthCount,
					average_sales: averageSales,
					due_inv_count: 0,
					late_due_inv_count: 0,
					late_percentage: 0,
					tier: '',
					recommend_limit: 0,
					previous_due_inv_count: 0,
					previous_late_due_inv_count: 0,
					previous_late_percentage: 0,
					previous_tier: '',
					current_limit: cards2[0].creditLimit,
					last_review: cards2[0].lastReviewDate
						? cards2[0].lastReviewDate
						: null,
					new_limit: null,
					status: cards2[0].status,
				};

				const lateCount = countLateInvoices(
					dueCards,
					koByDocNo,
					this.selectedAsOfDate,
				);

				const prevLateCount = prevDueCards
					? countLateInvoices(prevDueCards, koByDocNo, this.selectedAsOfDate)
					: 0;

				const latePercentage = roundToFixed(
					(lateCount / dueCount) * 100,
					this.numDecimal,
				);

				const prevLatePercentage = roundToFixed(
					(prevLateCount / prevDueCount) * 100,
					this.numDecimal,
				);

				object.late_percentage = dueCount !== 0 ? latePercentage : 0;
				object.due_inv_count = dueCount;
				object.late_due_inv_count = lateCount;
				object.tier =
					Math.round(totalIvdn) === 0
						? 'N/A'
						: latePercentage <= 10
						? 'A'
						: latePercentage > 10 && latePercentage <= 20
						? 'B'
						: 'C';
				object.recommend_limit =
					latePercentage <= 10
						? Math.round((averageSales * 8) / 100) * 100
						: latePercentage > 10 && latePercentage <= 20
						? Math.round((averageSales * 4) / 100) * 100
						: Math.round((averageSales * 2) / 100) * 100;

				object.previous_late_percentage = prevDueCount !== 0 ? prevLatePercentage : 0;
				object.previous_due_inv_count = prevDueCount;
				object.previous_late_due_inv_count = prevLateCount;
				object.previous_tier =
					Math.round(prevTotalIvdn) === 0
						? 'N/A'
						: prevLatePercentage <= 10
						? 'A'
						: prevLatePercentage > 10 && prevLatePercentage <= 20
						? 'B'
						: 'C';

				result.push(object);
			} else {
				const cards2 = groupedGlobalCustomer[code];

				const object = {
					customer: gdbx.customerNames[code],
					total_sales: 0,
					sales_month_count: 0,
					average_sales: 0,
					due_inv_count: 0,
					late_due_inv_count: 0,
					late_percentage: 0,
					previous_tier: 'N/A',
					tier: 'N/A',
					recommend_limit: 0,
					current_limit: cards2[0].creditLimit,
					last_review: cards2[0].lastReviewDate
						? cards2[0].lastReviewDate
						: null,
					new_limit: null,
					status: cards2[0].status,
				};
				result.push(object);
			}
		}
		this.tableItems = result;
		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
